import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";

import { Button } from "@mui/material";
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarExport, GridToolbarQuickFilter } from "@mui/x-data-grid";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { PaginationModel } from "../../../../../models/ReportGridModels";

interface UnPaidInvoicesResponseModel {
    data: UnPaidInvoicesData;
    error: IError;
}
interface UnPaidInvoicesData {
    invoices: XeroUnPaidInvoiceModel[];
}

interface XeroUnPaidInvoiceModel {
    id: string;
    invoiceId: string;
    contact: string;
    creationDate: Dayjs;
    dueDate: Dayjs;
    overdue: boolean;
    invoiceNumber: string;
    reference: string;
    status: string;
    type: string;
    subTotal: string;
    totalTax: string;
    total: string;
}

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarExport />
            <GridToolbarQuickFilter sx={{ marginLeft: "10px" }} />
        </GridToolbarContainer>
    );
}

const columnVisibility = {
    id: false,
}

function XeroInvoiceStatus() {
    const { enqueueSnackbar } = useSnackbar();
    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 200,
        page: 0,
    });

    const [unPaidInvoices, setUnPaidInvoices] = useState<XeroUnPaidInvoiceModel[]>([]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 10 },
        { field: 'contact', headerName: 'Contact', width: 250 },
        { // lastLoginDesktop
            field: 'creationDate', headerName: 'Creation Date', width: 110, renderCell: (param: any) => (
                <p>{dayjs(param.row.creationDate).format("YYYY-MM-DD")}</p>
            )
        },
        { // lastLoginDesktop
            field: 'dueDate', headerName: 'Due Date', width: 110, renderCell: (param: any) => (
                <p style={{ backgroundColor: param.row.overdue ? "red" : "transparent" }}>{dayjs(param.row.dueDate).format("YYYY-MM-DD")}</p>
            )
        },
        { field: 'invoiceNumber', headerName: 'Invoice Number', width: 120 },
        { field: 'reference', headerName: 'Reference', width: 400 },
        { field: 'status', headerName: 'Status', width: 120 },
        { field: 'type', headerName: 'Type', width: 100 },
        { field: 'subTotal', headerName: 'SubTotal', width: 100 },
        { field: 'totalTax', headerName: 'TotalTax', width: 100 },
        { field: 'total', headerName: 'Total', width: 100 },
        { // download
            field: 'quoteId', headerName: 'Download Quote', width: 160, renderCell: (param: any) => (
                <Button
                    variant="outlined"
                    onClick={() => DownloadPDF(param.row)}>
                    Download PDF
                </Button>
            )
        },
    ]

    const GetXeroInvoiceStatus = async () => {
        setUnPaidInvoices([]);

        try {
            var resp: UnPaidInvoicesResponseModel = await api.get("api/Xero/UnPaidInvoices", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setUnPaidInvoices(resp.data.invoices);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const DownloadPDF = async (invoice: XeroUnPaidInvoiceModel) => {
        const response = await api.get(`api/Xero/DownloadInvoice?invoiceId=${invoice.invoiceId}`, {
            headers: {
                "Authorization": localStorage.getItem("token")!
            }
        }).blob();

        const url = window.URL.createObjectURL(
            new Blob([response]),
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `${invoice.invoiceNumber}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    }

    const headerHeight: number = 130;
    function GetGridHeight() {
        return window.innerHeight - headerHeight;
    }

    return (
        <>
            <Button
                sx={{ marginTop: "-15px" }}
                variant="outlined"
                onClick={GetXeroInvoiceStatus}>
                Unpaid invoices
            </Button>

            <DataGrid
                sx={{ height: GetGridHeight(), marginTop: "5px", marginLeft: "-15px", marginRight: "-15px", marginBottom: "-20px" }}
                rows={unPaidInvoices}
                columns={columns}
                columnVisibilityModel={columnVisibility}
                pagination
                pageSizeOptions={[10, 15, 20, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                slots={{ toolbar: CustomToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 100 },
                    },
                }} />
        </>
    )
}

export default XeroInvoiceStatus;